/**
 * src/shell/agents/components/widgets/MeshTopologyWidget.tsx
 *
 * Real-Time Mesh Peer Connection Cluster.
 * Renders node ping latencies and connection states.
 */

import React, { useState, useEffect } from 'react';
import { IGenUIWidgetSpec } from '../../types/genui-types';
import { canvasTelemetryConnector } from '../../services/CanvasTelemetryConnector';

export interface MeshTopologyWidgetProps {
  spec: IGenUIWidgetSpec;
}

export const MeshTopologyWidget: React.FC<MeshTopologyWidgetProps> = ({ spec }) => {
  const [data, setData] = useState<Record<string, unknown>>({});

  useEffect(() => {
    return canvasTelemetryConnector.subscribe(
      spec.dataSource.endpoint,
      spec.dataSource.refreshIntervalMs || 2000,
      (payload) => setData(payload)
    );
  }, [spec.dataSource.endpoint, spec.dataSource.refreshIntervalMs]);

  const nodes = (data.nodes as Array<{ id: string; label: string; latency: number; status: string }>) || [
    { id: 'node_1', label: 'Edge-Alpha-01', latency: 24, status: 'online' },
    { id: 'node_2', label: 'Edge-Alpha-02', latency: 31, status: 'online' },
    { id: 'node_3', label: 'Relay-Beta-09', latency: 45, status: 'online' },
    { id: 'node_4', label: 'Core-Sentinel', latency: 12, status: 'online' },
  ];

  return (
    <div
      data-testid={`topology-widget-${spec.id}`}
      style={{
        background: 'rgba(15, 23, 42, 0.75)',
        border: '1px solid rgba(168, 85, 247, 0.3)',
        borderRadius: '14px',
        padding: '16px',
        color: '#f8fafc',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div>
          <div style={{ fontSize: '12px', fontWeight: 800 }}>{spec.title}</div>
          <div style={{ fontSize: '10px', color: '#94a3b8' }}>{spec.description}</div>
        </div>
        <span style={{ fontSize: '10px', fontWeight: 800, color: '#a855f7', background: 'rgba(168, 85, 247, 0.15)', padding: '2px 8px', borderRadius: '4px' }}>
          {nodes.length} MESH PEERS
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', flex: 1, alignItems: 'center' }}>
        {nodes.map((node) => (
          <div
            key={node.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              background: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 255, 255, 0.05)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }} />
              <span style={{ fontSize: '11px', fontWeight: 600 }}>{node.label}</span>
            </div>
            <span style={{ fontSize: '10px', color: '#38bdf8', fontWeight: 700 }}>
              {node.latency}ms
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
